import type {RefObject} from 'react';
import {useEffect, useRef, useState} from 'react';
import {
  hasUserSeenSMPPreviewCtaPulseForAddCover,
  hasUserSeenSMPViewToggleHighlightForAddCover,
  markUserHasSeenSMPViewToggleHighlightForAddCover,
  markUserSeenSMPPreviewCtaPulseForAddCover,
} from '@Libraries/social-media-wizard-cover-library';
import {SocialMediaWizardStep, socialAccountPostCoverValidations} from '@Components/social-media-wizard/social-media-wizard.types';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import {getFirstWizardStep} from '@Libraries/social-media-wizard-step-library';
import type {SocialAccount} from '@Components/social-media/account.vo';
import type {GraphicsMetaMapType} from '@Components/social-media/publish-params.types';
import {updateNextButtonIcon, updateNextButtonText, updatePost, updatePublishingAction, updateTriggerNext} from '@Components/social-media-wizard/social-media-wizard-reducer';
import type {SocialPost} from '@Components/social-media/post.vo';
import {PostState} from '@Components/social-media/post.vo';
import {deletePrefilledSchedulingDate} from '@Libraries/social-media-wizard-scheduling-library';
import {showSocialMediaPublishingUpsellDialog, SocialMediaUpsellEvent} from '@Libraries/social-media-account.library';
import {getCaptionErrorAccountId, getTitleErrorAccountId} from '@Libraries/social-media-params.library';
import {SocialPostPublishingAction} from '@Components/social-media/publish-params.types';
import {isUserPremium} from '@Libraries/user.library';
import {useAppDispatch} from '@/hooks';
import type {CaptionErrorByAccountIds, TitleErrorByAccountIds} from '@Components/social-media/social-media.types';

const VIEW_TOGGLE_HIGHLIGHT_VISIBILITY_TIME_MS = 6000;

interface UseWizardCoverCtaPulseOpts {
  currentStep: SocialMediaWizardStep;
  isMobileScreen: boolean;
  designGraphicsMetaMap: GraphicsMetaMapType;
  accountsToPublishTo: SocialAccount[];
}

interface UseWizardCoverCtaPulseResult {
  showPulsatingDotForPreviewCTA: boolean;
  showHightlightPulseForViewToggle: boolean;
  shouldTriggerPreviewCTAPulses: () => boolean;
  resetPreviewCTAPulsesStates: () => void;
  triggerPreviewCTAPulsesIfNeeded: () => void;
  hidePulsatingDotForPreviewCTAIfNeeded: () => void;
}

export function useWizardCoverCtaPulse({currentStep, isMobileScreen, designGraphicsMetaMap, accountsToPublishTo}: UseWizardCoverCtaPulseOpts): UseWizardCoverCtaPulseResult {
  const [showHightlightPulseForViewToggle, setShowHighlightPulseForViewToggle] = useState(false);
  const [showPulsatingDotForPreviewCTA, setShowPulsatingDotForPreviewCTA] = useState(false);
  const viewToggleHighlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return (): void => {
      if (viewToggleHighlightTimerRef.current !== null) {
        clearTimeout(viewToggleHighlightTimerRef.current);
      }
    };
  }, []);

  const isPostEligibleForCover = (): boolean => {
    const isSingleGraphic = Object.values(designGraphicsMetaMap).length === 1 && Object.values(Object.values(designGraphicsMetaMap)[0]).length === 1;

    if (!isSingleGraphic) {
      return false;
    }

    const isVideo = Object.values(Object.values(designGraphicsMetaMap)[0])[0].isVideo;
    if (!isVideo) {
      return false;
    }

    const accountsSupportingPostCoverToPublishTo = accountsToPublishTo.filter((account) => socialAccountPostCoverValidations[account.type]?.doesSupportCover);

    return accountsSupportingPostCoverToPublishTo.length >= 1;
  };

  const showNewFeaturePulseForAddCoverCTA = (): boolean => {
    return currentStep === SocialMediaWizardStep.ADD_DETAILS && isPostEligibleForCover();
  };

  const shouldTriggerPreviewCTAPulses = (): boolean => {
    if (!isMobileScreen) {
      return false;
    }

    return showNewFeaturePulseForAddCoverCTA();
  };

  const markUserSeenSMPPreviewCtaPulse = (): void => {
    if (showNewFeaturePulseForAddCoverCTA()) {
      void markUserSeenSMPPreviewCtaPulseForAddCover();
    }
  };

  const markUserHasSeenSMPViewToggleHighlight = (): void => {
    if (showNewFeaturePulseForAddCoverCTA()) {
      void markUserHasSeenSMPViewToggleHighlightForAddCover();
    }
  };

  const setTimerForUpdatingUserHasSeenViewToggleHighlight = (): void => {
    if (viewToggleHighlightTimerRef.current !== null) {
      clearTimeout(viewToggleHighlightTimerRef.current);
    }

    viewToggleHighlightTimerRef.current = setTimeout(() => {
      viewToggleHighlightTimerRef.current = null;
      setShowHighlightPulseForViewToggle(false);
      markUserHasSeenSMPViewToggleHighlight();
    }, VIEW_TOGGLE_HIGHLIGHT_VISIBILITY_TIME_MS);
  };

  const updateShowHightlightPulseForViewToggleState = async (): Promise<void> => {
    let hasUserSeen = true;

    if (showNewFeaturePulseForAddCoverCTA()) {
      hasUserSeen = await hasUserSeenSMPViewToggleHighlightForAddCover();
    }

    if (hasUserSeen) {
      return;
    }

    setShowHighlightPulseForViewToggle(true);
    setTimerForUpdatingUserHasSeenViewToggleHighlight();
  };

  const updateShowPulsatingDotForPreviewCtaState = async (): Promise<void> => {
    let hasUserSeen = true;

    if (showNewFeaturePulseForAddCoverCTA()) {
      hasUserSeen = await hasUserSeenSMPPreviewCtaPulseForAddCover();
    }

    if (hasUserSeen) {
      return;
    }

    setShowPulsatingDotForPreviewCTA(true);
  };

  const resetPreviewCTAPulsesStates = (): void => {
    if (!isMobileScreen) {
      return;
    }

    setShowPulsatingDotForPreviewCTA(false);
    setShowHighlightPulseForViewToggle(false);
  };

  const triggerPreviewCTAPulsesIfNeeded = (): void => {
    if (!shouldTriggerPreviewCTAPulses()) {
      return;
    }

    void updateShowPulsatingDotForPreviewCtaState();
    void updateShowHightlightPulseForViewToggleState();
  };

  const hidePulsatingDotForPreviewCTAIfNeeded = (): void => {
    if (!shouldTriggerPreviewCTAPulses() || !showPulsatingDotForPreviewCTA) {
      return;
    }

    setShowPulsatingDotForPreviewCTA(false);
    markUserSeenSMPPreviewCtaPulse();
  };

  return {
    showPulsatingDotForPreviewCTA,
    showHightlightPulseForViewToggle,
    shouldTriggerPreviewCTAPulses,
    resetPreviewCTAPulsesStates,
    triggerPreviewCTAPulsesIfNeeded,
    hidePulsatingDotForPreviewCTAIfNeeded,
  };
}

interface UseWizardDraftScheduleActionsOpts {
  socialPost: SocialPost | undefined;
  accountIdsToPublishTo: number[];
  captionErrors: CaptionErrorByAccountIds;
  titleErrors: TitleErrorByAccountIds;
}

interface UseWizardDraftScheduleActionsResult {
  isDateTimePickerVisible: boolean;
  setIsDateTimePickerVisible: (visible: boolean) => void;
  onSaveAsDraft: () => void;
  onSchedule: () => void;
  onSchedulingDone: () => void;
  onDeleteSchedule: () => void;
}

export function useWizardDraftScheduleActions({
  socialPost,
  accountIdsToPublishTo,
  captionErrors,
  titleErrors,
}: UseWizardDraftScheduleActionsOpts): UseWizardDraftScheduleActionsResult {
  const dispatch = useAppDispatch();
  const [isDateTimePickerVisible, setIsDateTimePickerVisible] = useState(false);

  const unschedulePost = (): void => {
    if (socialPost === undefined) {
      return;
    }

    void window.PMW.write(window.PMW.util.site_url('socialmedia/unschedulePost'), {idPost: socialPost.id});
  };

  const doDeleteScheduling = (): void => {
    if (socialPost?.publishOn && socialPost.timeZoneOffset !== null) {
      const {publishOn: _, timeZoneOffset: __, state: ___, ...unscheduledPost} = socialPost;
      const draftPost: SocialPost = {...unscheduledPost, state: PostState.DRAFT};
      deletePrefilledSchedulingDate();
      unschedulePost();
      dispatch(updatePost(draftPost));
    }
  };

  const onDeleteSchedule = (): void => {
    if (socialPost?.publishOn && socialPost.timeZoneOffset !== null) {
      doDeleteScheduling();
      dispatch(updateNextButtonText(window.i18next.t('pmwjs_publish')));
      dispatch(updateNextButtonIcon('icon-publish-arrow'));
    }
  };

  const onSaveAsDraft = (): void => {
    doDeleteScheduling();

    if (getCaptionErrorAccountId(accountIdsToPublishTo, captionErrors) !== null || getTitleErrorAccountId(accountIdsToPublishTo, titleErrors) != null) {
      dispatch(updateTriggerNext());
      return;
    }

    dispatch(updatePublishingAction(SocialPostPublishingAction.SAVE));
    dispatch(updateTriggerNext());
  };

  const onSchedulingDone = (): void => {
    if (getCaptionErrorAccountId(accountIdsToPublishTo, captionErrors) !== null || getTitleErrorAccountId(accountIdsToPublishTo, titleErrors) != null) {
      dispatch(updateTriggerNext());
      return;
    }

    dispatch(updatePublishingAction(SocialPostPublishingAction.SCHEDULE));
    dispatch(updateTriggerNext());
  };

  const onSchedule = (): void => {
    if (!isUserPremium()) {
      showSocialMediaPublishingUpsellDialog(SocialMediaUpsellEvent.SCHEDULE);
      return;
    }

    setIsDateTimePickerVisible(true);
  };

  return {
    isDateTimePickerVisible,
    setIsDateTimePickerVisible,
    onSaveAsDraft,
    onSchedule,
    onSchedulingDone,
    onDeleteSchedule,
  };
}

interface UseWizardNavStepOpts {
  wizardStep: SocialMediaWizardStep;
  isSelectTemplateFlow: boolean;
  navSteps: string[];
}

interface UseWizardNavStepResult {
  currNavStep: RefObject<number>;
  newNavStep: RefObject<number>;
}

export function useWizardNavStep({wizardStep, isSelectTemplateFlow, navSteps}: UseWizardNavStepOpts): UseWizardNavStepResult {
  const currNavStep = useRef<number>(0);
  const newNavStep = useRef<number>(0);
  const previousWizardStep = useRef(getFirstWizardStep());

  const isWizardStepInAccountsPhase = (step: SocialMediaWizardStep): boolean => {
    return (
      step === SocialMediaWizardStep.SELECT_ACCOUNTS ||
      step === SocialMediaWizardStep.ACCOUNT_CONNECTION ||
      step === SocialMediaWizardStep.ADD_DETAILS ||
      step === SocialMediaWizardStep.CONNECT_ACCOUNTS
    );
  };

  const isCurrentWizardStepMovingNext = (): boolean => {
    const currentWizardStepKey = Object.keys(SocialMediaWizardStep).indexOf(wizardStep);
    const previousWizardStepKey = Object.keys(SocialMediaWizardStep).indexOf(previousWizardStep.current);
    return currentWizardStepKey >= previousWizardStepKey;
  };

  useEffect((): void => {
    if (isWizardStepInAccountsPhase(wizardStep) && isWizardStepInAccountsPhase(previousWizardStep.current)) {
      return;
    }

    const isMovingForward = isCurrentWizardStepMovingNext();

    if (wizardStep === previousWizardStep.current && (isSelectTemplateFlow || navSteps?.length === 2)) {
      return;
    }

    if (isSelectTemplateFlow && wizardStep === SocialMediaWizardStep.ADD_DETAILS) {
      return;
    }

    previousWizardStep.current = wizardStep;

    if (isMovingForward) {
      newNavStep.current += 1;
    } else {
      newNavStep.current -= 1;
    }
  }, [wizardStep]);

  useDidUpdateEffect((): void => {
    currNavStep.current = newNavStep.current;
  }, [newNavStep.current]);

  return {
    currNavStep,
    newNavStep,
  };
}
